import Immutable from 'seamless-immutable';
import _ from 'lodash';
import RefData from 'ref-data';

import { baseCollection, filter } from '../../common/collections';
import channelService from '../../common/Services/channel-service';
import ChannelModel from './channel-model';

/**
 * Transforms and array of backend channel objects into a ChannelCollection
 * @param {Object[]} channels - backend formatted channels
 * @returns {Channel[]}
 */
function deserialize(channels = []) {
  return Immutable(_.map(channels, ChannelModel.deserialize));
}

/**
 * Transforms a ChannelCollection into an array of backend channel objects
 * @param {Channel[]} channels
 * @returns {Object[]}
 */
function serialize(channels) {
  return _.map(channels, (channel) => ChannelModel.serialize(channel));
}

/**
 * Takes a ChannelCollection and filters them by the provided publishFormatType
 * @param {RefData|string} publishFormatTypeRefOrId - the publish format type ref to filter the channels by
 * @param {Channel[]} channels - the channels to filter
 * @returns {Channel[]}
 */
function filterByPublishFormatTypeRef(publishFormatTypeRefOrId, channels) {
  const publishFormatTypeRef = _.isString(publishFormatTypeRefOrId)
    ? RefData.Model.hydrate(publishFormatTypeRefOrId)
    : publishFormatTypeRefOrId;
  return filter((channel) => channel.typeRef.id === publishFormatTypeRef.channel, channels);
}

/**
 * Binds a valid destination type for selecting a channel (i.e. microsite uses web channels)
 * @param {RefData} destinationTypeId - the publish format type ref
 * @returns {publishFormatTypeRefId}
 */
function prepareDestinationTypeForChannel(destinationTypeId) {
  switch (destinationTypeId) {
    case RefData.Enum.TYPE.DESTINATION.MICROSITE:
    case RefData.Enum.TYPE.DESTINATION.OVERLAY:
      return RefData.Enum.TYPE.DESTINATION.WEB;
    default:
      return destinationTypeId;
  }
}

/**
 *
 * @param {RefData|string} destinationType - the destination type to filter the channels by. possible values are web or social story
 * @param {Channel[]} channels - the channels to filter
 * @returns {Channel[]}
 */
function filterByDestinationType(destinationType, channels) {
  const destinationTypeRef = _.isString(destinationType) ? RefData.Model.hydrate(destinationType) : destinationType;
  const destinationTypeRefId = prepareDestinationTypeForChannel(destinationTypeRef.id);
  // innetwork exclusion: instagram channels typeRefId is story (same as facebook for example),
  // but need to reduce channels to use only instagram channels
  if (destinationTypeRefId === RefData.Enum.TYPE.DESTINATION.INSTAGRAM) {
    return filter((channel) => channel.typeRef.code === destinationTypeRef.code, channels);
  }
  return filter((channel) => channel.typeRef.destinationType === destinationTypeRefId, channels);
}

/**
 * Takes a ChannelColelction and returns those that are designated as live
 * @param {Channel[]} channels - the channels to filter
 * @returns {Channel[]}
 */
function filterByIsLive(channels) {
  return filter((channel) => channel.isLive && !channelService.isDeprecatedChannelType(channel.typeRef.id), channels);
}

/**
 * Takes a ChannelColelction and returns those that are designated as staging
 * @param {Channel[]} channels - the channels to filter
 * @returns {Channel[]}
 */
function filterByIsStaging(channels) {
  return filter((channel) => channel.isStaging, channels);
}

export default {
  ...baseCollection,
  create: deserialize,
  deserialize,
  serialize,

  filterByDestinationType,
  filterByPublishFormatTypeRef,
  filterByIsLive,
  filterByIsStaging,
};
